import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import CustomInput from "../../components/CustomInput/CustomInput";
import regexList from "../../utils/regexList";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loginUser } from "../../store/actions/auth.action";
import { getMicrosoftLoginUrl, login } from "../../endpoints/auth.service";
import { useSnackbar } from "notistack";
import TickOutline from "../../assets/svg/TickOutline";
import LoadingAnimation from "../../assets/svg/LoadingAnimation";
import Footer from "../../components/footer/footer";
import { EnumUserTypes } from "../../utils/constants";

const HiringCompanyLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      isLoading: false,
      keepMeSignedIn: false,
      email: "",
    },
  });
  const fields = watch();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const email = params.get("email") || params.get("login_hint");
    const sso = params.get("sso");

    if (email && (sso === "1" || sso === "true")) {
      window.location.replace(`/sso-login?email=${encodeURIComponent(email)}`);
    }
  }, []);

  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);

  const handleLogin = async (fields) => {
    try {
      setValue("isLoading", true);
      const { data } = await login(fields);

      const loginEmail = data?.user?.email || fields?.email;
      if (loginEmail) localStorage.setItem("ssoEmail", loginEmail);
      dispatch(loginUser(data));
    } catch (error) {
      if (error === "Otp not verified") {
        navigate(`/email-verification`, {
          state: {
            email: fields?.email,
          },
        });
        return;
      }
      enqueueSnackbar(error, { variant: "error" });
    } finally {
      setValue("isLoading", false);
    }
  };

  const handleMicrosoftLogin = async () => {
    try {
      setIsMicrosoftLoading(true);
      const { data, success } = await getMicrosoftLoginUrl(
        EnumUserTypes.HIRING_COMPANY,
        fields?.email,
      );
      if (success && data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error("Unable to start Microsoft login.");
    } catch (error) {
      enqueueSnackbar(error, { variant: "error" });
    } finally {
      setIsMicrosoftLoading(false);
    }
  };

  return (
    <div className="h-full w-full flex-1 gap-3 justify-center flex flex-col">
      {/* Form Container */}
      <div className="flex-1 flex justify-center relative">
        <div className="z-20 flex flex-col justify-center items-center border-[#DDDDDD] border rounded-lg py-10 px-16 bg-white relative h-max self-center w-full lg:w-[28%] ">
          <label className="font-semibold text-2xl mb-16">
            Hiring company login
          </label>

          <form
            className="flex flex-col w-full"
            onSubmit={handleSubmit(handleLogin)}
          >
            {/* @ts-ignore */}
            <CustomInput
              label={"Email"}
              name="email"
              placeholder={"Enter your Email"}
              rules={{
                required: "This field is required.",
                pattern: {
                  value: regexList.email,
                  message: "Please enter a valid email.",
                },
              }}
              control={control}
              errors={errors}
            />
            <div className="h-4" />
            {/* @ts-ignore */}
            <CustomInput
              label={"Password"}
              placeholder={"Enter your Password"}
              name="password"
              rules={{
                required: "This field is required.",
                pattern: {
                  value: regexList.password,
                  message: "Please enter a valid password.",
                },
              }}
              control={control}
              errors={errors}
            />

            {/* Checkbox container  */}
            <div className="flex  flex-col justify-between items-center w-full mt-3 text-light-black">
              <span
                onClick={() => navigate("/forgot-password")}
                className="font-medium text-[#03A9F4] text-xs cursor-pointer self-end"
              >
                Forgot password?
              </span>
            </div>

            <button
              type="submit"
              className="bg-[#6F6F6F] flex items-center justify-center rounded mt-10 self-end py-[10px] px-6"
            >
              {fields?.isLoading && <LoadingAnimation />}
              <span className="text-white font-medium">Login</span>
            </button>

            <div className="flex items-center gap-3 mt-6">
              <div className="h-px bg-[#DDDDDD] flex-1" />
              <span className="text-xs text-light-black">or</span>
              <div className="h-px bg-[#DDDDDD] flex-1" />
            </div>

            <button
              type="button"
              onClick={handleMicrosoftLogin}
              className="border border-[#DDDDDD] flex items-center justify-center rounded mt-6 self-end py-[10px] px-6"
              disabled={isMicrosoftLoading}
            >
              {isMicrosoftLoading && <LoadingAnimation />}
              <span className="text-light-black font-medium">
                Sign in with Microsoft
              </span>
            </button>
            <div className="flex justify-between items-center w-full mt-11 text-light-black">
              <div className="flex items-center gap-2">
                <div
                  onClick={() =>
                    setValue("keepMeSignedIn", !fields?.keepMeSignedIn)
                  }
                  className="border-2 border-[#6F6F6F] h-4 w-4 flex items-center justify-center cursor-pointer"
                >
                  {fields?.keepMeSignedIn ? (
                    <TickOutline className="w-1.5 h-1.5" fill="#6F6F6F" />
                  ) : null}
                </div>
                <span className="font-medium text-primary text-xs">
                  Keep me signed in
                </span>
              </div>
              <span className="self-end text-xs font-medium text-light-black">
                Don’t have an account?{" "}
                <span
                  onClick={() => navigate("/hiring/register")}
                  className="text-[#03A9F4] font-semibold cursor-pointer"
                >
                  Register
                </span>
              </span>
            </div>
          </form>
        </div>
        <div className="bg-slate-100 h-[45%] absolute w-full" />
      </div>

      <Footer />
    </div>
  );
};

export default HiringCompanyLoginPage;
